import { useEffect, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Lock } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';

// Saving a card for monthly billing - nothing is charged here.
//
// Used where a client signs up for block hours (the quote's signing
// dialog) and where an existing client sets up card billing from a link.
// The card goes straight into Stripe's own form; all our server ever
// receives is the setup intent's id, which it then asks Stripe about
// itself rather than believing the page.

interface Props {
  /** POST endpoint that starts the setup and returns the client secret. */
  startUrl: string;
  /** What will be charged and when, shown above the card form. */
  summary: React.ReactNode;
  /** Called with the confirmed setup intent id. */
  onSaved: (setupIntentId: string) => Promise<void>;
  buttonLabel: string;
  disabled?: boolean;
}

interface SetupResponse {
  clientSecret: string;
  publishableKey: string | null;
}

let stripePromise: Promise<Stripe | null> | null = null;
function stripeFor(publishableKey: string): Promise<Stripe | null> {
  stripePromise ??= loadStripe(publishableKey);
  return stripePromise;
}

export function CardSetupForm(props: Props): React.ReactElement {
  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch<SetupResponse>(props.startUrl, { method: 'POST', json: {} });
        if (!cancelled) setSetup(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Could not start card setup. Please try again in a moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.startUrl]);

  if (loading) return <p className="text-sm text-[var(--color-muted)]">Preparing a secure card form…</p>;

  if (error || !setup?.clientSecret || !setup.publishableKey) {
    return (
      <div className="rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm text-amber-900">
          {error ?? 'Card setup is unavailable right now. Please reply to the email that brought you here and we will set up billing another way.'}
        </p>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripeFor(setup.publishableKey)}
      options={{
        clientSecret: setup.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: { colorPrimary: '#54387E', colorText: '#2D2D2D', borderRadius: '8px', fontFamily: 'Inter, system-ui, sans-serif' },
        },
      }}
    >
      <CardFields {...props} />
    </Elements>
  );
}

function CardFields({ summary, onSaved, buttonLabel, disabled }: Props): React.ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await stripe.confirmSetup({ elements, redirect: 'if_required' });
      if (result.error) {
        setError(result.error.message ?? 'The card could not be saved.');
        return;
      }
      if (result.setupIntent?.status !== 'succeeded') {
        setError('The card was not saved. Nothing has been charged.');
        return;
      }
      await onSaved(result.setupIntent.id);
    } catch {
      setError('Something went wrong saving the card. Nothing has been charged.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-body)]">
        {summary}
      </div>
      <PaymentElement />
      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
      <Button onClick={() => void save()} disabled={disabled || busy || !stripe}>
        {busy ? 'Saving your card…' : buttonLabel}
      </Button>
      <p className="flex items-start gap-2 text-xs text-[var(--color-muted)]">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Card details go straight to Stripe and are never stored by T3. Nothing is charged today; the card is
        kept on file for monthly billing, as set out in the Master Services Agreement.
      </p>
    </div>
  );
}
